import {debounce} from '@github/mini-throttle/decorators'
import {COPILOT_AUTH_TOKEN_KEY} from '@github-ui/copilot-auth-token'
import type {
  CopilotChatModel,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotChatSettings,
  CopilotSpaceTab,
  FileDiffReference,
  IndexCopilotSpace,
  MediaContentItem,
} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import safeStorage from '@github-ui/safe-storage'
import {useCallback, useState} from 'react'

import {referenceID} from './copilot-chat-helpers'
import type {SkuType} from './sku-types'

interface MessageRequestID {
  messageID: string
  requestID: string
}

type EntrypointMessage = {
  message: string
  references?: CopilotChatReference[]
  model?: CopilotChatModel
  mediaContent?: MediaContentItem[]
  branch?: string
}

/**
 * Source of the saved message, used to differentiate messages from different parts of the application.
 * - 'dashboard': Messages saved from the chat input partial on the dashboard
 * - 'immersive': Messages saved from the immersive or embedded chat views
 * - 'task': Messages saved from the cloud agent task input
 * - 'spark': Messages saved from the Spark input
 * - 'spaces': Messages saved from the Copilot Spaces view
 */
export type SavedMessageSource = 'dashboard' | 'immersive' | 'task' | 'spark' | 'spaces'

export class CopilotLocalStorage {
  private COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY = 'COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY'
  private COPILOT_SELECTED_THREAD_ID_STORAGE_KEY = 'COPILOT_SELECTED_THREAD_ID_STORAGE_KEY'
  private COPILOT_PULL_REQUEST_THREAD_MAP_STORAGE_KEY = 'COPILOT_PULL_REQUEST_THREAD_MAP_STORAGE_KEY'
  private COPILOT_COLLAPSED_STATE_KEY = 'COPILOT_COLLAPSED_STATE_KEY'
  private COPILOT_MINIMIZED_STATE_KEY = 'COPILOT_MINIMIZED_STATE_KEY'
  private COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY = 'COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY'
  private COPILOT_PANEL_HEIGHT = 'COPILOT_PANEL_HEIGHT'
  private DEFAULT_PANEL_HEIGHT = 600
  private COPILOT_PANEL_WIDTH = 'COPILOT_PANEL_WIDTH'
  private COPILOT_PANEL_POSITION_RIGHT = 'COPILOT_PANEL_POSITION_RIGHT'
  private COPILOT_PANEL_POSITION_BOTTOM = 'COPILOT_PANEL_POSITION_BOTTOM'
  private DEFAULT_PANEL_POSITION_RIGHT = 8
  private DEFAULT_PANEL_POSITION_BOTTOM = 8
  // Visual gap reserved between the top of the assistive panel and the bottom of
  // the page header when computing the default panel height.
  private DEFAULT_PANEL_HEADER_GAP = 16
  // Fallback page-header bottom (in px) used when the `.AppHeader` element is
  // not yet present in the DOM at the time we compute the default height.
  private DEFAULT_HEADER_BOTTOM_FALLBACK = 64
  private COPILOT_ENTRYPOINT_MESSAGE_KEY = (threadID: string | undefined | null) =>
    threadID ? `COPILOT_ENTRYPOINT_MESSAGE_KEY_${threadID}` : 'COPILOT_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY = 'COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY = 'COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY = 'COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_MESSAGE_REQUEST_ID_KEY = 'COPILOT_MESSAGE_REQUEST_ID_KEY'
  private COPILOT_METER_BANNER_FLAG = 'COPILOT_METER_BANNER_FLAG'
  private COPILOT_QUOTA_EXCEEDED_BANNER_FLAG = 'COPILOT_QUOTA_EXCEEDED_BANNER_FLAG'
  private COPILOT_QUOTA_BANNER_FLAG = 'COPILOT_QUOTA_BANNER_FLAG'
  private COPILOT_ANIMATION_FLAG = 'COPILOT_ANIMATION_FLAG'

  private COPILOT_WRAP_CODE_LINES_KEY = 'COPILOT_WRAP_CODE_LINES_KEY'
  private COPILOT_TRACING_REQUESTED_FLAG = 'COPILOT_TRACING_REQUESTED_FLAG'
  private COPILOT_DIFF_REFERENCES_KEY = 'COPILOT_DIFF_REFERENCES_KEY'
  private COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT = 'COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT'
  private COPILOT_SPACES_SOURCES_TIP_DISMISSAL_COUNT = 'COPILOT_SPACES_SOURCES_TIP_DISMISSAL_COUNT'
  private COPILOT_SPACES_SELECTED_TAB = 'COPILOT_SPACES_SELECTED_TAB'
  private COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE = 'COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE'
  private COPILOT_LAST_USED_REPOSITORY_KEY = 'COPILOT_LAST_USED_REPOSITORY_KEY'
  private COPILOT_ACTIVE_MODE_KEY = 'COPILOT_ACTIVE_MODE_KEY'
  private COPILOT_SELECTED_MODEL_VERSION_KEY = 'COPILOT_SELECTED_MODEL_VERSION'
  private COPILOT_LANDED_ON_AGENTS_VIEW_KEY = 'COPILOT_LANDED_ON_AGENTS_VIEW_KEY'
  private lastCheckedModelVersion: string | null = null

  private MAX_MESSAGE_REQUEST_ID_STORAGE_COUNT = 100
  DEFAULT_PANEL_WIDTH = 560

  // Version for model selection storage. Increment this in code to trigger future clearing.
  private CURRENT_MODEL_STORAGE_VERSION = '15'

  private COPILOT_SELECTED_MODEL_STORAGE_KEY = (threadID: string | null) => `COPILOT_SELECTED_MODEL_${threadID}`

  private COPILOT_SETTINGS_STORAGE_KEY = (threadID: string | null) => `COPILOT_SETTINGS_${threadID}`

  private COPILOT_SAVED_USER_MESSAGE_KEY = (threadID: string | null, source: SavedMessageSource): string =>
    `COPILOT_SAVED_USER_MESSAGE_${source}_${threadID}`

  private COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY = (threadID: string | null): string =>
    `COPILOT_SAVED_USER_MESSAGE_ON_ERROR_${threadID}`

  private COPILOT_CURRENT_REFERENCES_KEY = (threadID: string | null): string => `COPILOT_CURRENT_REFERENCES_${threadID}`

  private COPILOT_SELECTED_TOPIC_STORAGE_KEY = (threadId: string): string => `COPILOT_SELECTED_TOPIC_${threadId}`

  private localStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24, // 1 day
  })

  private mediumStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24 * 28, // 28 days - for model selection persistence
  })

  private longStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
  })

  private tempStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 30, // 30 minutes
  })

  private sessionStorage = safeStorage('sessionStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24, // 1 day
  })

  get selectedThreadID(): string | null {
    return this.localStorage.getItem(this.COPILOT_SELECTED_THREAD_ID_STORAGE_KEY)
  }

  set selectedThreadID(threadID: string | null) {
    const key = this.COPILOT_SELECTED_THREAD_ID_STORAGE_KEY
    if (threadID == null) {
      this.localStorage.removeItem(key)
      return
    }
    this.localStorage.setItem(key, threadID)
  }

  getPullRequestThreadID(urlString?: string): string | null {
    const pullRequestKey = this.getPullRequestKey(urlString)
    if (!pullRequestKey) return null

    const threadMap = this.getPullRequestThreadMap()
    return threadMap[pullRequestKey] || null
  }

  setPullRequestThreadID(threadID: string | null, urlString?: string): void {
    const pullRequestKey = this.getPullRequestKey(urlString)
    if (!pullRequestKey) return

    const threadMap = this.getPullRequestThreadMap()
    if (threadID) {
      threadMap[pullRequestKey] = threadID
    } else {
      delete threadMap[pullRequestKey]
    }

    this.mediumStorage.setItem(this.COPILOT_PULL_REQUEST_THREAD_MAP_STORAGE_KEY, JSON.stringify(threadMap))
  }

  private getPullRequestThreadMap(): Record<string, string> {
    const threadMap = this.mediumStorage.getItem(this.COPILOT_PULL_REQUEST_THREAD_MAP_STORAGE_KEY)
    if (!threadMap) return {}

    try {
      const parsed: unknown = JSON.parse(threadMap)
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return {}
      }
      // Filter to only string values to guarantee the Record<string, string> contract
      const result: Record<string, string> = {}
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'string') {
          result[key] = value
        }
      }
      return result
    } catch {
      return {}
    }
  }

  private getPullRequestKey(urlString?: string): string | null {
    if (typeof window === 'undefined' && !urlString) return null

    // Use a dummy base when a urlString is supplied so we never touch window.location during SSR.
    // new URL ignores the base when the first argument is already absolute.
    const url = urlString
      ? new URL(urlString, 'https://github.com')
      : new URL(window.location.href, window.location.origin)
    const match = url.pathname.match(/^\/([^/]+)\/([^/]+)\/pull\/(\d+)(?:\/|$)/)
    if (!match) return null

    const owner = match[1]
    const repo = match[2]
    const pullRequestNumber = match[3]
    if (!owner || !repo || !pullRequestNumber) return null

    return `${decodeURIComponent(owner)}/${decodeURIComponent(repo)}:${pullRequestNumber}`
  }

  getModel(threadID: string | null): CopilotChatModel | null {
    // Auto-clear model selection for new threads if version changed (when FF is enabled)
    if (this.lastCheckedModelVersion !== this.CURRENT_MODEL_STORAGE_VERSION && threadID === null) {
      this.checkAndClearModelSelection()
    }

    const json = this.mediumStorage.getItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(threadID))
    return json === null ? json : (JSON.parse(json) as CopilotChatModel)
  }

  setModel(threadID: string | null, value: CopilotChatModel | null, updateDefault = true) {
    this.mediumStorage.setItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(threadID), JSON.stringify(value))
    // If in a thread and updateDefault is true, also update the model for new conversations.
    // This ensures the default model persists when user explicitly changes it, but not when simply browsing old conversations.
    if (threadID && updateDefault) {
      this.mediumStorage.setItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(null), JSON.stringify(value))
    }
  }

  /**
   * Clears cached model selection for new threads when version changes.
   * Only runs when clearModelSelectionForDefaultChange FF is enabled.
   * Only clears null thread to allow existing threads to keep their selections.
   */
  private checkAndClearModelSelection(): void {
    // Update the last checked version to prevent redundant checks
    this.lastCheckedModelVersion = this.CURRENT_MODEL_STORAGE_VERSION

    // Only proceed if feature flag is enabled
    if (!copilotFeatureFlags.clearModelSelectionForDefaultChange) {
      return
    }

    const storedVersion = this.longStorage.getItem(this.COPILOT_SELECTED_MODEL_VERSION_KEY)

    if (storedVersion !== this.CURRENT_MODEL_STORAGE_VERSION) {
      this.mediumStorage.removeItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(null))

      // Update version to prevent clearing again
      this.longStorage.setItem(this.COPILOT_SELECTED_MODEL_VERSION_KEY, this.CURRENT_MODEL_STORAGE_VERSION)
    }
  }

  get settings(): CopilotChatSettings | null {
    const json = this.localStorage.getItem(this.COPILOT_SETTINGS_STORAGE_KEY(this.selectedThreadID))
    return json === null ? json : (JSON.parse(json) as CopilotChatSettings)
  }

  set settings(value: CopilotChatSettings) {
    this.localStorage.setItem(this.COPILOT_SETTINGS_STORAGE_KEY(this.selectedThreadID), JSON.stringify(value))
  }

  /**
   * Returns the user's persisted panel height when present, otherwise a
   * viewport-derived default. `panelBottom` is the bottom offset (in px) at
   * which the calling host actually renders the panel — it is only relevant
   * to the dynamic-default path and is used to keep `header gap` consistent
   * across hosts:
   *  - Standard assistive `ChatPanel`: omit (or pass `DEFAULT_PANEL_POSITION_BOTTOM`);
   *    the panel is anchored at the default bottom by CSS.
   *  - Draggable embedded host: pass `getPanelPositionBottom()` so that if the
   *    user has dragged the panel up, the default height shrinks accordingly
   *    and the rendered top still sits `DEFAULT_PANEL_HEADER_GAP` below the
   *    page header.
   */
  getPanelHeight(panelBottom?: number): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_HEIGHT)
    if (!val) return this.getDefaultPanelHeight(panelBottom)
    const parsed = parseInt(val)
    return isNaN(parsed) ? this.getDefaultPanelHeight(panelBottom) : parsed
  }

  /**
   * Returns the panel height for the initial render of the resizable-panel
   * hook without performing a DOM measurement. When the user has a
   * persisted height we return it; otherwise we return the static fallback
   * (`DEFAULT_PANEL_HEIGHT`). Callers should use this on mount and then
   * call `getPanelHeight()` later (e.g. when the panel opens) to swap in
   * the viewport-derived default. This avoids forcing a synchronous
   * style/layout read on every page that mounts the chat host while the
   * panel is still closed.
   */
  getInitialPanelHeight(): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_HEIGHT)
    if (!val) return this.DEFAULT_PANEL_HEIGHT
    const parsed = parseInt(val)
    return isNaN(parsed) ? this.DEFAULT_PANEL_HEIGHT : parsed
  }

  /**
   * Returns true when the user has previously persisted a panel height (a
   * numeric value is stored). Consumers can use this to decide whether to
   * refresh the dynamic default height when the viewport or page header
   * changes — once a user has resized the panel, we must respect their
   * stored value and not overwrite it.
   */
  hasStoredPanelHeight(): boolean {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_HEIGHT)
    if (!val) return false
    return !isNaN(parseInt(val))
  }

  /**
   * Compute the default assistive panel height so that, before any user resize,
   * the top of the panel sits roughly `DEFAULT_PANEL_HEADER_GAP` pixels below
   * the bottom of the page header. This keeps the gap visually consistent
   * across breakpoints since it is derived from the actual rendered header.
   *
   * The `panelBottom` argument is the bottom offset (in px) at which the
   * caller actually renders the panel. The standard assistive `ChatPanel` is
   * CSS-anchored at `DEFAULT_PANEL_POSITION_BOTTOM`, so it should omit the
   * argument (or pass that constant). The draggable embedded host can pass
   * `getPanelPositionBottom()` so that — when the user has dragged the panel
   * up — the default height shrinks by the same amount, preserving the
   * header gap. If the persisted bottom were used unconditionally the
   * standard panel would open ~(persistedBottom - defaultBottom)px shorter
   * than intended whenever the embedded panel had been dragged up.
   */
  private getDefaultPanelHeight(panelBottom: number = this.DEFAULT_PANEL_POSITION_BOTTOM): number {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return this.DEFAULT_PANEL_HEIGHT
    }
    const header = document.querySelector<HTMLElement>('.AppHeader')
    const headerBottom = header?.getBoundingClientRect().bottom ?? this.DEFAULT_HEADER_BOTTOM_FALLBACK
    const available = window.innerHeight - headerBottom - this.DEFAULT_PANEL_HEADER_GAP - panelBottom
    // Never shrink below the previous static default to preserve usability on
    // very short viewports; the resizable-panel hook re-clamps this value
    // against the actual viewport (window.innerHeight - 1rem - 60px, matching
    // the CSS `max-height` cap on `.copilotChatPanel`) on mount and during
    // resize/keyboard interactions.
    return Math.max(this.DEFAULT_PANEL_HEIGHT, available)
  }

  setPanelHeight(height: number) {
    const key = this.COPILOT_PANEL_HEIGHT
    this.localStorage.setItem(key, height.toString())
  }

  getPanelWidth(): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_WIDTH)
    if (!val) return this.DEFAULT_PANEL_WIDTH
    const parsed = parseInt(val)
    return isNaN(parsed) ? this.DEFAULT_PANEL_WIDTH : parsed
  }

  setPanelWidth(width: number) {
    const key = this.COPILOT_PANEL_WIDTH
    this.localStorage.setItem(key, width.toString())
  }

  /**
   * Returns true when the user has previously persisted a panel width (a
   * numeric value is stored). Consumers can use this to decide whether to
   * overwrite the stored width with a viewport-clamped value — when no
   * value has been persisted, the resizable-panel hook is rendering the
   * default width and clamping it on a narrow viewport should not be
   * baked into storage (otherwise opening later on a wider viewport
   * would use the accidental stored value instead of the default).
   */
  hasStoredPanelWidth(): boolean {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_WIDTH)
    if (!val) return false
    return !isNaN(parseInt(val))
  }

  getPanelPositionRight(): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_POSITION_RIGHT)
    if (!val) return this.DEFAULT_PANEL_POSITION_RIGHT
    const parsed = parseInt(val)
    return isNaN(parsed) ? this.DEFAULT_PANEL_POSITION_RIGHT : parsed
  }

  setPanelPositionRight(right: number) {
    this.localStorage.setItem(this.COPILOT_PANEL_POSITION_RIGHT, right.toString())
  }

  getPanelPositionBottom(): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_POSITION_BOTTOM)
    if (!val) return this.DEFAULT_PANEL_POSITION_BOTTOM
    const parsed = parseInt(val)
    return isNaN(parsed) ? this.DEFAULT_PANEL_POSITION_BOTTOM : parsed
  }

  setPanelPositionBottom(bottom: number) {
    this.localStorage.setItem(this.COPILOT_PANEL_POSITION_BOTTOM, bottom.toString())
  }

  getRepoCustomInstructionsState(): boolean {
    const val = this.localStorage.getItem(this.COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY)
    // NOTE: repo custom instructions are enabled by default
    return val !== 'false'
  }

  setRepoCustomInstructionsState(enabled: boolean) {
    const key = this.COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY
    this.localStorage.setItem(key, enabled.toString())
  }

  getCollapsedState(): boolean {
    const val = this.sessionStorage.getItem(this.COPILOT_COLLAPSED_STATE_KEY)
    return val !== 'false'
  }

  setCollapsedState(collapsed: boolean) {
    const key = this.COPILOT_COLLAPSED_STATE_KEY
    this.sessionStorage.setItem(key, collapsed.toString())
  }

  getMinimizedState(): boolean {
    const key = this.COPILOT_MINIMIZED_STATE_KEY
    const state = this.sessionStorage.getItem(key)
    if (state === 'minimized') return true

    if (state !== null) this.sessionStorage.removeItem(key)
    return false
  }

  setMinimizedState(minimized: boolean) {
    const key = this.COPILOT_MINIMIZED_STATE_KEY
    if (minimized) {
      this.sessionStorage.setItem(key, 'minimized')
    } else {
      this.sessionStorage.removeItem(key)
    }
  }

  getSelectedTopic(threadID: string): string | null {
    return this.localStorage.getItem(this.COPILOT_SELECTED_TOPIC_STORAGE_KEY(threadID))
  }

  setSelectedTopic(threadID: string, topic: string | null) {
    if (topic == null) {
      this.localStorage.removeItem(this.COPILOT_SELECTED_TOPIC_STORAGE_KEY(threadID))
      return
    }

    const key = this.COPILOT_SELECTED_TOPIC_STORAGE_KEY(threadID)
    this.localStorage.setItem(key, topic)
  }

  getSavedMessage(threadID: string | null, source: SavedMessageSource): string | null {
    return this.localStorage.getItem(this.COPILOT_SAVED_USER_MESSAGE_KEY(threadID, source))
  }

  @debounce(100)
  setSavedMessage(threadID: string | null, source: SavedMessageSource, message: string | null) {
    this.setSavedMessageFast(threadID, source, message)
  }

  // Same as setSavedMessage but without debounce
  // Used when we want to set/reset the message immediately (e.g. when sending user message to a new thread)
  setSavedMessageFast(threadID: string | null, source: SavedMessageSource, message: string | null) {
    const key = this.COPILOT_SAVED_USER_MESSAGE_KEY(threadID, source)

    if (message == null) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, message)
  }

  clearSavedUserMessage(threadID: string | null, source: SavedMessageSource) {
    this.localStorage.removeItem(this.COPILOT_SAVED_USER_MESSAGE_KEY(threadID, source))
  }

  getSavedUserMessageOnError(threadID: string | null): string | null {
    return this.localStorage.getItem(this.COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY(threadID))
  }

  setSavedUserMessageOnError(threadID: string | null, message: string | null) {
    const key = this.COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY(threadID)

    if (message == null) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, message)
  }

  clearSavedUserMessageOnError(threadID: string | null) {
    this.localStorage.removeItem(this.COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY(threadID))
  }

  getEntrypointMessage(threadID?: string | null) {
    return this.getEntrypointMessageImpl(this.COPILOT_ENTRYPOINT_MESSAGE_KEY(threadID))
  }

  private getEntrypointMessageImpl(key: string): {
    message: string
    references?: CopilotChatReference[]
    model?: CopilotChatModel
    mediaContent?: MediaContentItem[]
    branch?: string
  } | null {
    const entrypointMessage = this.localStorage.getItem(key)
    if (entrypointMessage == null) {
      return null
    }
    let parsedMessage: EntrypointMessage | undefined
    try {
      parsedMessage = JSON.parse(entrypointMessage)
    } catch {
      return null
    }
    if (!parsedMessage || !parsedMessage.message) {
      return null
    }
    return {
      message: parsedMessage.message,
      references: parsedMessage.references,
      model: parsedMessage.model,
      mediaContent: parsedMessage.mediaContent,
      branch: parsedMessage.branch,
    }
  }

  clearEntrypointMessage(threadID?: string | null): void {
    this.localStorage.removeItem(this.COPILOT_ENTRYPOINT_MESSAGE_KEY(threadID))
  }

  setEntrypointMessage({
    message,
    references,
    model,
    mediaContent,
    threadID,
  }: {
    message: string | null
    references?: CopilotChatReference[]
    model?: CopilotChatModel
    mediaContent?: MediaContentItem[]
    threadID?: string | null
  }): void {
    this.setEntrypointMessageImpl({
      message,
      references,
      model,
      mediaContent,
      key: this.COPILOT_ENTRYPOINT_MESSAGE_KEY(threadID),
    })
  }

  private setEntrypointMessageImpl({
    message,
    references,
    model,
    mediaContent,
    branch,
    key,
  }: {
    message: string | null
    references?: CopilotChatReference[]
    model?: CopilotChatModel
    mediaContent?: MediaContentItem[]
    branch?: string
    key: string
  }): void {
    if (message == null) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, JSON.stringify({message, references, model, mediaContent, branch}))
  }

  getTaskEntrypointMessage(): {message: string; references?: CopilotChatReference[]; branch?: string} | null {
    return this.getEntrypointMessageImpl(this.COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY)
  }

  setTaskEntrypointMessage(message: string | null, references?: CopilotChatReference[], branch?: string): void {
    this.setEntrypointMessageImpl({message, references, branch, key: this.COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY})
  }

  getLoopEntrypointMessage(): {message: string; references?: CopilotChatReference[]} | null {
    return this.getEntrypointMessageImpl(this.COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY)
  }

  setLoopEntrypointMessage(message: string | null, references?: CopilotChatReference[]): void {
    this.setEntrypointMessageImpl({message, references, key: this.COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY})
  }

  getSparkEntrypointMessage(): {message: string; references?: CopilotChatReference[]} | null {
    return this.getEntrypointMessageImpl(this.COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY)
  }

  setSparkEntrypointMessage(message: string | null): void {
    this.setEntrypointMessageImpl({message, key: this.COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY})
  }

  getLastUsedRepository(): CopilotChatRepo | null {
    const val = this.localStorage.getItem(this.COPILOT_LAST_USED_REPOSITORY_KEY)
    if (!val) return null

    try {
      return JSON.parse(val) as CopilotChatRepo
    } catch {
      return null
    }
  }

  setLastUsedRepository(reference: CopilotChatRepo): void {
    this.localStorage.setItem(this.COPILOT_LAST_USED_REPOSITORY_KEY, JSON.stringify(reference))
  }

  getCurrentReferences(threadID: string | null): CopilotChatReference[] | null {
    const key = this.COPILOT_CURRENT_REFERENCES_KEY(threadID)

    const val = this.localStorage.getItem(key)
    if (!val) return null

    return JSON.parse(val) as CopilotChatReference[]
  }

  setCurrentReferences(threadID: string | null, references: CopilotChatReference[]): void {
    const key = this.COPILOT_CURRENT_REFERENCES_KEY(threadID)
    const uniqueReferences = references.filter((ref, index) => {
      return index === references.findIndex(r => referenceID(ref) === referenceID(r))
    })
    this.localStorage.setItem(key, JSON.stringify(uniqueReferences))
  }

  clearCurrentReferences(threadID: string | null): void {
    const key = this.COPILOT_CURRENT_REFERENCES_KEY(threadID)
    this.localStorage.removeItem(key)
  }

  migrateNullThreadToNewThread(newThreadID: string, source: SavedMessageSource): void {
    const savedMessage = this.getSavedMessage(null, source)
    this.setSavedMessageFast(null, source, null)
    if (savedMessage) this.setSavedMessageFast(newThreadID, source, savedMessage)

    const savedRefs = this.getCurrentReferences(null)
    this.clearCurrentReferences(null)
    if (savedRefs) this.setCurrentReferences(newThreadID, savedRefs)

    const nullThreadSettings = this.settings
    if (nullThreadSettings) {
      this.localStorage.setItem(this.COPILOT_SETTINGS_STORAGE_KEY(newThreadID), JSON.stringify(nullThreadSettings))
    }
    this.selectedThreadID = newThreadID
    this.localStorage.removeItem(this.COPILOT_SETTINGS_STORAGE_KEY(null))
  }

  getImmersiveSidebarCollapsedState(): boolean {
    const item = this.localStorage.getItem(this.COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY)
    return item === null || item === 'true'
  }

  setImmersiveSidebarCollapsedState(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY, String(state))
  }

  getCopilotDiffReferences(): FileDiffReference[] | null {
    const val = this.localStorage.getItem(this.COPILOT_DIFF_REFERENCES_KEY)
    if (!val) return null

    return JSON.parse(val) as FileDiffReference[]
  }

  setCopilotDiffReferences(fileDiffReferences: FileDiffReference | FileDiffReference[]): void {
    const referencesArray = Array.isArray(fileDiffReferences) ? fileDiffReferences : [fileDiffReferences]
    this.localStorage.setItem(this.COPILOT_DIFF_REFERENCES_KEY, JSON.stringify(referencesArray))
  }

  removeCopilotDiffReferences(): void {
    this.localStorage.removeItem(this.COPILOT_DIFF_REFERENCES_KEY)
  }

  clearAuthToken(): void {
    this.localStorage.removeItem(COPILOT_AUTH_TOKEN_KEY)
  }

  getMeterBannerFlag(): boolean | null {
    const value = this.localStorage.getItem(this.COPILOT_METER_BANNER_FLAG)
    if (value === null) {
      return null
    }
    return value === 'true'
  }

  setMeterBannerFlag(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_METER_BANNER_FLAG, String(state))
  }

  removeMeterBannerFlag(): void {
    this.localStorage.removeItem(this.COPILOT_METER_BANNER_FLAG)
  }

  getQuotaExceededBannerFlag(): boolean | null {
    const value = this.tempStorage.getItem(this.COPILOT_QUOTA_EXCEEDED_BANNER_FLAG)
    if (value === null) {
      return null
    }
    return value === 'true'
  }

  setQuotaExceededBannerFlag(state: boolean): void {
    this.tempStorage.setItem(this.COPILOT_QUOTA_EXCEEDED_BANNER_FLAG, String(state))
  }

  removeQuotaExceededBannerFlag(): void {
    this.tempStorage.removeItem(this.COPILOT_QUOTA_EXCEEDED_BANNER_FLAG)
  }

  getQuotaBannerFlag(variant: string): boolean | null {
    const value = this.localStorage.getItem(`${this.COPILOT_QUOTA_BANNER_FLAG}:${variant}`)
    if (value === null) return null
    return value === 'true'
  }

  setQuotaBannerFlag(variant: string, visible: boolean): void {
    this.localStorage.setItem(`${this.COPILOT_QUOTA_BANNER_FLAG}:${variant}`, String(visible))
  }

  removeQuotaBannerFlag(variant: string): void {
    this.localStorage.removeItem(`${this.COPILOT_QUOTA_BANNER_FLAG}:${variant}`)
  }

  getSkuSuccessBannerFlag(skuType: SkuType): boolean | null {
    return this.getBooleanFlag(this.skuSuccessBannerKey(skuType))
  }

  setSkuSuccessBannerFlag(skuType: SkuType, state: boolean): void {
    this.tempStorage.setItem(this.skuSuccessBannerKey(skuType), String(state))
  }

  removeSkuSuccessBannerFlag(skuType: SkuType): void {
    this.tempStorage.removeItem(this.skuSuccessBannerKey(skuType))
  }

  getAnimationFlag(): boolean | null {
    return this.getBooleanFlag(this.COPILOT_ANIMATION_FLAG)
  }

  setAnimationFlag(state: boolean): void {
    this.tempStorage.setItem(this.COPILOT_ANIMATION_FLAG, String(state))
  }

  removeAnimationFlag(): void {
    this.tempStorage.removeItem(this.COPILOT_ANIMATION_FLAG)
  }

  getTracingRequestedFlag(): boolean {
    return this.localStorage.getItem(this.COPILOT_TRACING_REQUESTED_FLAG) === 'true'
  }

  setTracingRequestedFlag(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_TRACING_REQUESTED_FLAG, String(state))
  }

  getWrapCodeLines(): boolean {
    const item = this.localStorage.getItem(this.COPILOT_WRAP_CODE_LINES_KEY)
    return item === 'true'
  }

  setWrapCodeLines(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_WRAP_CODE_LINES_KEY, String(state))
  }

  getBooleanFlag(key: string, storage = this.tempStorage): boolean | null {
    const value = storage.getItem(key)
    if (value === null) {
      return null
    }
    return value === 'true'
  }

  getSpacesMcpBannerDismissalCount(): number {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT)
    return val ? parseInt(val, 10) : 0
  }

  setSpacesMcpBannerDismissalCount(count: number) {
    const key = this.COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT
    this.localStorage.setItem(key, count.toString())
  }

  getSpacesSourcesTipDismissalCount(): number {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_SOURCES_TIP_DISMISSAL_COUNT)
    return val ? parseInt(val, 10) : 0
  }

  setSpacesSourcesTipDismissalCount(count: number) {
    const key = this.COPILOT_SPACES_SOURCES_TIP_DISMISSAL_COUNT
    this.localStorage.setItem(key, count.toString())
  }

  getSpacesSelectedTab(): CopilotSpaceTab {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_SELECTED_TAB)
    return (val as CopilotSpaceTab) || 'personal'
  }

  setSpacesSelectedTab(tab: CopilotSpaceTab) {
    const key = this.COPILOT_SPACES_SELECTED_TAB
    this.localStorage.setItem(key, tab)
  }

  getLastSelectedSpace(): IndexCopilotSpace | undefined {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE)
    if (!val) return undefined

    try {
      return JSON.parse(val) as IndexCopilotSpace
    } catch {
      return undefined
    }
  }

  setLastSelectedSpace(space: IndexCopilotSpace | undefined): void {
    const key = this.COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE
    if (space === undefined) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, JSON.stringify(space))
  }

  getActiveMode(): string | null {
    return this.longStorage.getItem(this.COPILOT_ACTIVE_MODE_KEY)
  }

  setActiveMode(modeName: string): void {
    this.longStorage.setItem(this.COPILOT_ACTIVE_MODE_KEY, modeName)
  }

  getLandedOnAgentsView(): boolean {
    return this.sessionStorage.getItem(this.COPILOT_LANDED_ON_AGENTS_VIEW_KEY) === 'true'
  }

  setLandedOnAgentsView(state: boolean): void {
    this.sessionStorage.setItem(this.COPILOT_LANDED_ON_AGENTS_VIEW_KEY, String(state))
  }

  // Get the success banner storage key for a Copilot SKU
  private skuSuccessBannerKey(skuType: SkuType): string {
    return `COPILOT_${skuType.toUpperCase()}_SUCCESS_BANNER_FLAG`
  }

  /**
   * @returns an array of objects with messageID and requestID
   * @description This function retrieves the message request IDs from local storage and validates them.
   * It ensures that the data is in the expected format and filters out any invalid entries.
   */
  private retrieveAndValidateRequestIDs(): MessageRequestID[] {
    const currentIDs = this.localStorage.getItem(this.COPILOT_MESSAGE_REQUEST_ID_KEY)
    try {
      const parsedValues = JSON.parse(currentIDs || '[]')
      if (!Array.isArray(parsedValues) || parsedValues.length === 0) {
        return []
      }
      const validIDs = parsedValues.filter((item: {messageID: string; requestID: string}) => {
        return typeof item.messageID === 'string' && typeof item.requestID === 'string'
      })
      return validIDs as MessageRequestID[]
    } catch {
      // if parsing fails, just return an empty array
      return []
    }
  }

  /**
   * Adds a requestID keyed on messageID to localstorage.
   * The tuple of (requestID, messageID) is stored in localstorage.
   * If the messageID already exists, it will be replaced with the new requestID.
   * If the number of stored requestIDs exceeds the maximum limit, the oldest one will be removed.
   * @param messageID messageID
   * @param requestID requestID -- Assigned by the server to each request
   */
  addRequestID(messageID: string, requestID: string): void {
    const currentIDs = this.retrieveAndValidateRequestIDs()
    // Check if the messageID already exists
    // This really should not happen because the tuple of (messageID, requestID) is unique
    // but if it does, we need to remove the old one
    const existingIndex = currentIDs.findIndex(item => item.messageID === messageID)
    if (existingIndex !== -1) {
      // If it exists, remove it from the array
      currentIDs.splice(existingIndex, 1)
    }
    currentIDs.push({messageID, requestID})
    if (currentIDs.length > this.MAX_MESSAGE_REQUEST_ID_STORAGE_COUNT) {
      currentIDs.shift()
    }
    this.localStorage.setItem(this.COPILOT_MESSAGE_REQUEST_ID_KEY, JSON.stringify(currentIDs))
  }

  /**
   * Retrieved the requestID for a given messageID if it exists
   * @param messageID messageID for which you want to get the requestID
   * @returns requestID or undefined
   */
  getRequestID(messageID: string): string | undefined {
    const currentIDs = this.retrieveAndValidateRequestIDs()
    const foundID = currentIDs.find(item => item.messageID === messageID)
    return foundID ? foundID.requestID : undefined
  }
}

export const copilotLocalStorage = new CopilotLocalStorage()

/** All methods of CopilotLocalStorage that take a single argument of type T */
type CopilotLocalStorageUnaryMethod<T> = {
  [K in keyof CopilotLocalStorage as CopilotLocalStorage[K] extends (arg: T) => unknown
    ? K
    : never]: CopilotLocalStorage[K]
}

/** All methods of CopilotLocalStorage that take no arguments */
type CopilotLocalStorageNullaryMethod<T> = {
  [K in keyof CopilotLocalStorage as CopilotLocalStorage[K] extends () => T ? K : never]: CopilotLocalStorage[K]
}

/**
 * A React hook that provides a way to get and set values in CopilotLocalStorage.
 * It takes a getter and setter method from CopilotLocalStorage and returns the current value and a setter function.
 * The setter function updates both the local state and the CopilotLocalStorage.
 */
export function useCopilotLocalStorage<T>(
  getter: keyof CopilotLocalStorageNullaryMethod<T>,
  setter: keyof CopilotLocalStorageUnaryMethod<T>,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => copilotLocalStorage[getter]() as T)
  const wrappedSetter = useCallback(
    (newValue: T) => {
      ;(copilotLocalStorage[setter] as (arg: T) => void)(newValue)
      setValue(newValue)
    },
    [setter],
  )
  return [value, wrappedSetter]
}
